/**
 * park-* design-system — base/helper module.
 *
 * Every `park-*` custom element imports from here. This is the *only* shared
 * runtime in the library; it is deliberately tiny and dependency-free (no lit,
 * no framework). The contract it establishes is the keystone of the whole
 * library (epic #138 / spike #139):
 *
 *   - LIGHT DOM ONLY. No shadow DOM, ever. Elements render their markup straight
 *     into `this` (`this.innerHTML`), so the page's already-loaded global.css +
 *     tokens.css + park.css cascade reaches them and admin/editor tooling can see
 *     real DOM. There are NO per-component `<style>` blocks: styling comes from
 *     the shared stylesheets and from `kg-*` / `park-*` utility classes.
 *   - Components are pure functions of their attributes + their slotted (author-
 *     provided) inner HTML. Re-render on attribute change.
 *   - The bundle (#140) is an IIFE whose entry is `index.ts`; importing every
 *     element module registers all tags and sets `window.ParkDS`.
 *
 * See `./README.md` for the full element contract + a copy-paste skeleton.
 */

/** Shape published on `window.ParkDS` by the bundle entry (`index.ts`). */
export interface ParkDSGlobal {
	/** Library version (bumped in `index.ts`). */
	version: string;
	/** Tag names successfully registered with `customElements`, in load order. */
	elements: string[];
}

/**
 * Tags registered so far, in registration order. Populated as a side effect of
 * each element module's `defineElement(...)` call. `index.ts` snapshots this
 * into `window.ParkDS.elements` after importing every element module.
 */
export const registeredTags: string[] = [];

/**
 * Register a custom element, guarding against double registration.
 *
 * Safe to call repeatedly: HMR, a re-evaluated bundle, or the same element
 * module being imported twice will NOT throw the `customElements.define`
 * "already defined" `DOMException`. No-ops cleanly when there is no DOM
 * (e.g. accidental SSR import), so the module stays import-safe everywhere.
 */
export function defineElement(
	tag: string,
	ctor: CustomElementConstructor,
): void {
	// No DOM (SSR / non-browser): nothing to register, stay import-safe.
	if (typeof customElements === "undefined") return;

	if (customElements.get(tag)) {
		// Already defined (HMR / double bundle load) — keep the registry honest
		// but do not redefine.
		if (!registeredTags.includes(tag)) registeredTags.push(tag);
		return;
	}

	customElements.define(tag, ctor);
	registeredTags.push(tag);
}

/**
 * Escape a value for safe interpolation into HTML *text* or a double-quoted
 * *attribute*. Use this for any value that is not statically known to be safe
 * (attribute values, anything derived from user/admin input). Slotted author
 * HTML (`this.slotHtml`) is intentionally NOT escaped — it is the page author's
 * own trusted markup.
 */
export function esc(value: unknown): string {
	return String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

/**
 * Minimal tagged-template helper. It is *just* a readable string joiner (gives
 * editors HTML highlighting via the `html` tag); it does NOT auto-escape
 * interpolations. Wrap untrusted values with {@link esc}. Returns a string,
 * which the caller assigns to `this.innerHTML` (usually via `render()`).
 *
 *   render() {
 *     return html`<a class="kg-btn" href="${esc(this.attr("href"))}">${this.slotHtml}</a>`;
 *   }
 */
export function html(
	strings: TemplateStringsArray,
	...values: unknown[]
): string {
	let out = "";
	for (let i = 0; i < strings.length; i++) {
		out += strings[i];
		if (i < values.length) out += String(values[i] ?? "");
	}
	return out;
}

/**
 * Base class for every `park-*` element.
 *
 * Lifecycle (light DOM):
 *   1. `connectedCallback` (first time): capture the element's original inner
 *      HTML into `this.slotHtml` (the "slot"), then render.
 *   2. `attributeChangedCallback`: re-render when an *observed* attribute
 *      actually changes value (after the first connect).
 *   3. `render()` returns the full inner HTML string; the base assigns it to
 *      `this.innerHTML` and then calls the optional `afterRender()` hook
 *      (where a subclass may wire event listeners on the freshly-rendered DOM).
 *
 * Subclasses MUST:
 *   - declare `static get observedAttributes(): string[]`
 *   - implement `render(): string`
 *
 * Subclasses MUST NOT:
 *   - attach a shadow root
 *   - emit `<style>` (styling lives in global.css / park.css + utility classes)
 */
// SSR-safe base. On the server `HTMLElement` is undefined; the element classes
// are never instantiated there (defineElement no-ops), but `extends HTMLElement`
// is evaluated at *import* time — so the ds/*.astro SSR wrappers, which import
// the pure render fns from these same modules, would crash with "HTMLElement is
// not defined". Fall back to a stub base class on the server so the modules stay
// import-safe in Node while real elements still extend HTMLElement in the browser.
const ElementBase: typeof HTMLElement =
	typeof HTMLElement !== "undefined"
		? HTMLElement
		: (class {} as unknown as typeof HTMLElement);

export abstract class ParkElement extends ElementBase {
	/**
	 * The element's original inner HTML, captured once at first connect — the
	 * light-DOM equivalent of a default `<slot>`. Re-renders reuse it so author-
	 * provided content (e.g. a button label) survives attribute changes.
	 */
	protected slotHtml = "";

	private _initialized = false;

	/**
	 * True when the element's markup was produced server-side (the `.astro`
	 * wrapper stamps a `data-ssr` attribute). In that case the server HTML is
	 * authoritative — the element does NOT re-render on connect, so SSR content
	 * stays in the initial HTML for SEO / no-JS. (Live park pages never mutate a
	 * component's attributes at runtime, so a re-render is never needed there; the
	 * Claude-Design tool creates elements WITHOUT `data-ssr`, so they render
	 * client-side as normal.)
	 */
	private _ssr = false;

	/** Return the element's full inner HTML for the current attribute state. */
	protected abstract render(): string;

	/**
	 * Optional post-render hook. Runs after every `innerHTML` write, so it is the
	 * place to (re)bind listeners to the just-rendered children. Default: no-op.
	 */
	protected afterRender(): void {
		/* no-op by default */
	}

	connectedCallback(): void {
		if (!this._initialized) {
			// Isomorphic hydration: if the server already rendered this element
			// (the `.astro` wrapper added `data-ssr`), trust that markup and do
			// NOT overwrite it — keeps SSR content authoritative for SEO/no-JS.
			if (this.hasAttribute("data-ssr")) {
				this._ssr = true;
				this._initialized = true;
				this.removeAttribute("data-ssr");
				return;
			}
			this.slotHtml = this.innerHTML.trim();
			this._initialized = true;
		}
		this._update();
	}

	attributeChangedCallback(
		_name: string,
		oldValue: string | null,
		newValue: string | null,
	): void {
		// Ignore the initial attribute set (fires before connect) and no-op
		// changes; connect drives the first render. SSR-hydrated elements are
		// server-authoritative (no captured slot) — never re-render them.
		if (this._initialized && !this._ssr && oldValue !== newValue) {
			this._update();
		}
	}

	private _update(): void {
		this.innerHTML = this.render();
		this.afterRender();
	}

	/** Get an attribute value, falling back to `fallback` when absent. */
	protected attr(name: string, fallback = ""): string {
		return this.getAttribute(name) ?? fallback;
	}

	/** Boolean-attribute getter (presence test), e.g. `disabled`. */
	protected boolAttr(name: string): boolean {
		return this.hasAttribute(name);
	}
}
